import { useState } from 'react';
import { Box, Typography, IconButton, TextField, Button, Tooltip, InputAdornment } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

import proposalsServices from '../../services/proposalsServices';

const fontFamily = '"Inter", sans-serif';

const ProposalViewWorkDescription = ({ items, proposalId, onRefresh, editable }) => {
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [saving, setSaving] = useState(false);

  if (!items || items.length === 0) return null;

  const handleStartEdit = (idx) => {
    setEditingIdx(idx);
    setEditValue(items[idx].description || '');
    setEditPrice(items[idx].price || '');
  };

  const handleCancel = () => {
    setEditingIdx(null);
    setEditValue('');
    setEditPrice('');
  };

  const handleSave = async () => {
    if (!editValue.trim()) {
      toast.error('Description cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const updated = items.map((item, idx) =>
        idx === editingIdx
          ? { ...item, description: editValue.trim(), price: Number(editPrice) || 0 }
          : item
      );

      const response = await proposalsServices.update(proposalId, { workDescriptions: updated });
      if (response.status === 200) {
        toast.success('Work Description updated');
        setEditingIdx(null);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save Work Description');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="print-section" sx={{ mb: 4, px: 2 }}>
      {/* Work Description Header */}
      <Box sx={{ display: 'inline-block', mb: 0 }}>
        <Box
          sx={{
            backgroundColor: '#0d3c61',
            color: '#fff',
            px: 2,
            py: 0.75,
          }}
        >
          <Typography
            sx={{
              fontFamily,
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Work Description
          </Typography>
        </Box>
      </Box>

      <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, borderTopLeftRadius: 0, overflow: 'hidden' }}>
        {items.map((item, idx) => (
          <Box
            key={item.id || idx}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              p: 1.5,
              borderBottom: idx < items.length - 1 ? '1px solid #e0e0e0' : 'none',
            }}
          >
            {editingIdx === idx ? (
              /* Edit mode */
              <Box className="no-print" sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Price"
                    sx={{ width: { xs: 100, sm: 130 } }}
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 20 } }} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saving}
                      sx={{ minWidth: 'auto', px: { xs: 1.5, sm: 2 } }}
                    >
                      <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 }, mr: { xs: 0, sm: 0.5 } }} />
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Cancel</Box>
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ minWidth: 'auto', px: { xs: 1.5, sm: 2 }, backgroundColor: '#0d3c61', '&:hover': { backgroundColor: '#0a2d4a' } }}
                    >
                      <SaveIcon sx={{ fontSize: { xs: 18, sm: 20 }, mr: { xs: 0, sm: 0.5 } }} />
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{saving ? 'Saving...' : 'Save'}</Box>
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              /* View mode */
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: '#4caf50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.2,
                  }}
                >
                  <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>✓</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily, fontSize: 13, color: '#333', lineHeight: 1.6 }}>
                    {item.description}
                  </Typography>
                </Box>
                {editable && (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleStartEdit(idx)}
                      className="no-print"
                      sx={{ color: '#0d3c61', '@media print': { display: 'none !important' } }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ProposalViewWorkDescription;
